/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import { Component, ReactNode, useEffect, useState } from "react";
import { APIProvider, Map, AdvancedMarker, useMap, Pin } from "@vis.gl/react-google-maps";
import * as Sentry from "@sentry/react";
import { MdMyLocation } from "react-icons/md";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getLocationCoordinates } from "@Services/utils/helpers";
import { DELHI_CORDINATES } from "@Services/constants";
import { useTranslation } from "@i18n";

/** A lat/lng pair, as the Maps SDK and this component's callers exchange them. */
type LatLng = { lat: number; lng: number };

/** The reverse-geocoded address this component publishes alongside the picked pin. */
type StructuredAddress = {
  fullAddress?: string;
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
};

/** The subset of a Geocoder result this file reads. */
type GeocoderAddressResult = {
  address_components: Array<{ types: string[]; long_name: string }>;
  formatted_address: string;
};

/** The `gmp-select` event the new PlaceAutocompleteElement fires; not modelled by @types/google.maps. */
type PlaceSelectEvent = {
  placePrediction: {
    toPlace: () => {
      fetchFields: (options: { fields: string[] }) => Promise<unknown>;
      /** Passed straight to `map.fitBounds`, so it is named by that method's parameter type. */
      viewport?: Parameters<NonNullable<ReturnType<typeof useMap>>["fitBounds"]>[0];
      location: { lat: () => number; lng: () => number };
    };
  };
};

type PlaceAutocompleteElement = HTMLElement & {
  addEventListener(type: "gmp-select", listener: (event: PlaceSelectEvent) => void | Promise<void>): void;
};

/**
 * The Google Maps JS global as this file reaches it. `@types/google.maps` declares the `google`
 * namespace but not `window.google`, and two members are used in ways its class declarations do not
 * model (the autocomplete element is constructed with no options, and `gmp-select` is a custom event),
 * so the surface is named here instead.
 */
type WindowWithGoogleMaps = Window & {
  google?: {
    maps: {
      event: { removeListener: (listener: unknown) => void };
      ControlPosition: Record<string, number>;
      Geocoder: new () => {
        geocode: (
          request: { location: LatLng },
          callback: (results: GeocoderAddressResult[], status: string) => void,
        ) => void;
      };
      places: { PlaceAutocompleteElement: new () => PlaceAutocompleteElement };
    };
  };
};

type GoogleMapProps = {
  allowPanning?: boolean;
  allowManualSelection?: boolean;
  additionalMarker?: LatLng | null;
  defaultCenter?: LatLng | null;
  allowFullScreen?: boolean;
  allowRecenterBtns?: boolean;
  allowSearch?: boolean;
  autoFetchLiveLocation?: boolean;
  getLocationCallBack?: (args: {
    current: LatLng | null;
    selected: LatLng | null;
    selectedPinAddress: StructuredAddress;
    mapZoomLevel?: number;
  }) => void;
  zoomLevel?: number;
  fetchAddress?: boolean;
  showLiveLocation?: boolean;
};

/**
 * Contains failures coming from the Google Maps subtree so they don't bubble to the
 * app-level ErrorBoundary and take down the whole page.
 *
 * Google's Maps JS bundle can load in a degraded/partial state (e.g. auth/referrer
 * failure), where @vis.gl/react-google-maps still reports the API as loaded and
 * resolves the "marker" library, but constructing a PinElement/AdvancedMarker throws
 * deep inside the maps bundle ("Cannot read properties of undefined (reading 'keys')").
 * That throw happens in a React effect, so it propagates to the nearest error boundary.
 */
class MapErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // Keep Sentry visibility, but as a scoped/handled event instead of a page crash.
    Sentry.captureException(error, { tags: { component: "GoogleMap" }, extra: { info } });
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

function GoogleMap({
  allowPanning = true,
  allowManualSelection = true,
  additionalMarker = null,
  defaultCenter = null,
  allowFullScreen = true,
  allowRecenterBtns = true,
  allowSearch = true,
  autoFetchLiveLocation = true,
  getLocationCallBack,
  zoomLevel = 14,
  fetchAddress = false,
  showLiveLocation = true,
}: GoogleMapProps) {
  const [currentPosition, setCurrentPosition] = useState<LatLng | null>(defaultCenter);
  const [searchedPosition, setSearchedPosition] = useState<LatLng | null>(additionalMarker);
  // const [isLocationDenied, setIsLocationDenied] = useState(true);
  const [selectedPinAddress, setSelectedPinAddress] = useState<StructuredAddress>({});
  const [mapZoomLevel, setMapZoomLevel] = useState<number | undefined>(zoomLevel);
  const [isLoaded, setIsLoaded] = useState(false);
  const { t } = useTranslation();

  const RecenterBtn = () => {
    const map = useMap();

    useEffect(() => {
      if (zoomLevel) {
        if (map) {
          map.setZoom(zoomLevel);
        }
      }
    }, [zoomLevel]);

    const handleCurrentRecenter = () => {
      if (map && currentPosition) {
        map.panTo(currentPosition);
      }
    };
    const handleSelectedRecenter = () => {
      if (map && searchedPosition) {
        map.panTo(searchedPosition);
      }
    };

    useEffect(() => {
      handleSelectedRecenter();
    }, [searchedPosition]);

    useEffect(() => {
      if (!map) return;
      const listener = map.addListener("dragend", () => {
        setMapZoomLevel(map.getZoom());
      });
      return () => {
        if (listener) {
          (window as WindowWithGoogleMaps).google?.maps?.event?.removeListener(listener);
        }
      };
    }, [map]);

    useEffect(() => {
      if (!map || !allowSearch) return;

      const placeAutocomplete = new (window as WindowWithGoogleMaps).google!.maps.places.PlaceAutocompleteElement();
      placeAutocomplete.setAttribute("placeholder", "Search for a place");
      placeAutocomplete.id = "place-autocomplete-input";
      const card = document.createElement("div");
      card.id = "place-autocomplete-card";
      card.style.background = "white";
      card.style.borderRadius = "0.25rem";
      card.style.borderColor = "white";
      card.appendChild(placeAutocomplete);

      const controlsArray = map.controls?.[(window as WindowWithGoogleMaps).google!.maps.ControlPosition.TOP_LEFT];
      controlsArray?.push(card);
      placeAutocomplete.addEventListener("gmp-select", async ({ placePrediction }) => {
        const place = placePrediction.toPlace();
        await place.fetchFields({ fields: ["displayName", "formattedAddress", "location"] });
        if (place.viewport) {
          map.fitBounds(place.viewport);
        }
        const searchedLocation = { lat: place.location.lat(), lng: place.location.lng() };
        map.panTo(searchedLocation);
        setSearchedPosition(() => searchedLocation);
        fetchPinAddress(searchedLocation);
      });

      // Cleanup on unmount
      return () => {
        const cleanupArray = map.controls?.[(window as WindowWithGoogleMaps).google!.maps.ControlPosition.TOP_LEFT];
        if (cleanupArray && cleanupArray.getLength?.() > 0) {
          cleanupArray.pop();
        }
      };
    }, [map]);

    return allowRecenterBtns ? (
      <>
        <button
          onClick={handleSelectedRecenter}
          style={{
            position: "absolute",
            insetInlineEnd: "0.5625rem",
            bottom: "5rem",
            zIndex: 999,
            padding: "0.5rem 0.625rem",
            cursor: "pointer",
            borderRadius: "50%",
            background: "white",
          }}
        >
          <ToolTipComponent content={t("Recenter to selected location")} position="bottom">
            <MdMyLocation size={24} color="#FBBC04" />
          </ToolTipComponent>
        </button>
        {showLiveLocation && (
          <button
            onClick={handleCurrentRecenter}
            style={{
              position: "absolute",
              insetInlineEnd: "0.5625rem",
              bottom: "8.125rem",
              zIndex: 999,
              padding: "0.5rem 0.625rem",
              cursor: "pointer",
              borderRadius: "50%",
              background: "white",
            }}
          >
            <ToolTipComponent content={t("Recenter to live location")} position="top">
              <MdMyLocation size={24} color="#EA4335" />
            </ToolTipComponent>
          </button>
        )}
      </>
    ) : (
      ""
    );
  };

  const fetchPinAddress = (latLng: LatLng) => {
    if (!(window as WindowWithGoogleMaps).google?.maps?.Geocoder) return;
    const geocoder = new (window as WindowWithGoogleMaps).google!.maps.Geocoder();

    geocoder.geocode({ location: latLng }, (results: GeocoderAddressResult[], status: string) => {
      if (status === "OK" && results[0]) {
        const components = results[0].address_components;

        const getComponent = (type: string) => components.find((c) => c.types.includes(type))?.long_name || "";

        const street = getComponent("route");
        const streetNumber = getComponent("street_number");
        const locality = getComponent("locality");
        const postalCode = getComponent("postal_code");
        const state = getComponent("administrative_area_level_1");
        const country = getComponent("country");

        const fullAddress = results[0]!.formatted_address;

        const structuredAddress = {
          fullAddress,
          street: `${streetNumber} ${street}`.trim(),
          city: locality,
          state,
          country,
          postalCode,
        };
        setSelectedPinAddress(structuredAddress);
      }
    });
  };

  useEffect(() => {
    if (additionalMarker) {
      setSearchedPosition(additionalMarker);
    }
  }, [additionalMarker]);

  useEffect(() => {
    if (defaultCenter) {
      setCurrentPosition(defaultCenter);
    }
  }, [defaultCenter]);

  useEffect(() => {
    if (searchedPosition && fetchAddress && isLoaded) {
      fetchPinAddress(searchedPosition);
    }
  }, [searchedPosition, isLoaded, fetchAddress]);

  useEffect(() => {
    if (getLocationCallBack) {
      getLocationCallBack({ current: currentPosition, selected: searchedPosition, selectedPinAddress, mapZoomLevel });
    }
  }, [searchedPosition, currentPosition, selectedPinAddress, mapZoomLevel]);

  useEffect(() => {
    updatePositionWithCurrentLocation();
  }, []);

  const updatePositionWithCurrentLocation = async () => {
    if (autoFetchLiveLocation) {
      const currentLocation = (await getLocationCoordinates()) as number[];
      const location = {
        lat: currentLocation[0]!,
        lng: currentLocation[1]!,
      };
      if (showLiveLocation) {
        setCurrentPosition(location);
      }
      if (!additionalMarker) {
        setSearchedPosition(location);
        fetchPinAddress(location);
      }
    } else if (!additionalMarker) {
      setSearchedPosition(DELHI_CORDINATES);
      fetchPinAddress(DELHI_CORDINATES);
    }
  };

  const handlePointClick = (args: { detail: { latLng: LatLng | null } }) => {
    if (allowManualSelection) {
      setSearchedPosition(args.detail.latLng);
      fetchPinAddress(args.detail.latLng!);
    }
  };

  return currentPosition || searchedPosition ? (
    <MapErrorBoundary
      fallback={
        <div className="flex items-center justify-center h-full w-full text-gray-500">
          {t("Map is currently unavailable")}
        </div>
      }
    >
      <APIProvider
        onLoad={() => setIsLoaded(true)}
        apiKey={process.env.REACT_APP_GOOGLE_KEY || ""}
        libraries={["places"]}
      >
        <Map
          onClick={handlePointClick}
          mapId="d1b0b06a3bb418a339d8e0f6"
          defaultCenter={searchedPosition || currentPosition}
          defaultZoom={mapZoomLevel}
          streetViewControl={false}
          disableDefaultUI={!allowPanning}
          mapTypeControl={false}
          disableDoubleClickZoom={true}
          draggable={allowPanning}
          fullscreenControl={allowFullScreen}
        >
          <RecenterBtn />
          {showLiveLocation && isLoaded && <AdvancedMarker position={currentPosition} />}
          {searchedPosition && isLoaded && (
            <AdvancedMarker position={searchedPosition}>
              <Pin background={"#FBBC04"} glyphColor={"#000"} borderColor={"#000"} />
            </AdvancedMarker>
          )}
        </Map>
      </APIProvider>
    </MapErrorBoundary>
  ) : (
    <div>Map Loading...</div>
  );
}
export default GoogleMap;
